import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useClickSound } from '../hooks/useClickSound';

interface FullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  showCloseButton?: boolean;
}

export default function FullScreenModal({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
}: FullScreenModalProps) {
  const { withClickSound } = useClickSound();
  const isMobile = window.innerWidth < 768;

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: isMobile ? 300 : 200, // Higher z-index on mobile to be above bottom nav
    animation: 'fadeIn 0.3s ease-out',
  };

  const modalStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: isMobile ? 301 : 201, // Higher z-index on mobile to be above bottom nav
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(10, 10, 20, 0.98)',
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    animation: isMobile ? 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)' : 'fadeIn 0.3s ease-out',
    overflow: 'hidden',
    width: '100vw',
    maxWidth: '100vw',
    height: '100vh',
    maxHeight: '100vh',
  };

  const headerStyle: CSSProperties = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: isMobile ? '16px' : '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(10, 10, 20, 0.5)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  const contentStyle: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    paddingBottom: isMobile ? '80px' : '0', // Add padding for bottom nav on mobile
    minWidth: 0,
    minHeight: 0,
  };

  const closeButtonStyle: CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'rgba(255, 255, 255, 0.9)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    transition: 'all 0.2s ease',
  };

  return (
    <>
      <div style={backdropStyle} onClick={withClickSound(onClose)} />
      <div style={modalStyle}>
        {(title || showCloseButton) && (
          <div style={headerStyle}>
            {title && (
              <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'white', margin: 0 }}>
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={withClickSound(onClose)}
                style={closeButtonStyle}
                aria-label="Close"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                ×
              </button>
            )}
          </div>
        )}
        <div style={contentStyle} className="custom-scrollbar">
          {children}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
